import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { TaxReason } from 'src/features/taxReasons/utils/types';
import TaxReasonFormModal from 'src/features/taxReasons/components/forms/TaxReasonFormModal';
import { useUpdateTaxReason } from 'src/features/taxReasons/api/useUpdateTaxReason';
import { LIST_TAX_REASONS_RESOURCE } from 'src/features/taxReasons/api/useListTaxReasons';
import { taxReasonsMessages } from 'src/features/taxReasons/utils/messages';

interface UpdateTaxReasonFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTaxReason: TaxReason;
    children?: React.ReactNode;
}

function UpdateTaxReasonFormModal<TData>({
                                                   className,
                                                   semantics,
                                                   children,
                                                   selectedTaxReason,
                                               }: UpdateTaxReasonFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateTaxReason({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_TAX_REASONS_RESOURCE] });
            toast({
                title: formatMessage(taxReasonsMessages.updateTaxReasonSuccess),
                description: formatMessage(taxReasonsMessages.updateTaxReasonSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <TaxReasonFormModal
            selectedTaxReason={selectedTaxReason}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, id: selectedTaxReason.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(taxReasonsMessages.editTaxReasonSubtitle, {
                entity: selectedTaxReason.name,
            })}
        >
            {children}
        </TaxReasonFormModal>
    );
}

export default UpdateTaxReasonFormModal;
